// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise';

import { useAgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { EditableCell, type EditorComponent, type EditableCellProps } from 'helpers/ag-grid/EditableCells/EditableCell';
import type { RequireAtLeastOne } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

export type EditableColDef<
  TData,
  TKey extends keyof TData,
  TValue = TData[TKey],
  EComponent extends EditorComponent<TValue> = EditorComponent<TValue>,
> = Omit<ColDef<TData>, 'field' | 'editable' | 'valueGetter'> &
  Pick<EditableCellProps<TValue, EComponent>, 'Editor' | 'closeOnChange' | 'clearable' | 'dropdownPadding'> & {
    /**
     * Defines when the cell is editable.
     */
    editable: ((data: TData) => boolean) | boolean;
    editorProps?:
      | EditableCellProps<TValue, EComponent>['editorProps']
      | ((data: TData) => EditableCellProps<TValue, EComponent>['editorProps']);
    /**
     * Any footer to display in the Editor
     */
    editorFooter?: ((data: TData) => ReactNode) | ReactNode;
  } & {
    /**
     * Gets the value from your data for display.
     *
     * Type-safe version of AG Grid's valueGetter to ensure that the output matches `TValue`
     */
    valueGetter?: (params: ValueGetterParams<TData, TValue>) => TValue | null | undefined;
  } & RequireAtLeastOne<DataMapper<TData, TKey, TValue>>;

/** Defines how to map the new value onto the data, such that it can be used in the Patch call */
type DataMapper<TData, TKey extends keyof TData, TValue = TData[TKey]> = {
  /**
   * The field of the row object to get the cell's data from.
   *
   * Also used to determine which field in `TData` to update for the eventual Patch call.
   * */
  field?: TKey & ColDef<TData>['field'];
  /**
   * Used to map the updated `value` onto the data `TData` object
   * for use in the Patch call.
   *
   * Only should be used when `field` is not sufficient.
   * */
  dataMapper?: (data: TData, updatedValue: TValue | null) => Partial<TData> | Promise<Partial<TData>>;
};

/**
 * Popover used for in-line editing of cells.
 *
 * Use via `editableColDef`.
 */
const EditableCellPopover = <TData, TKey extends keyof TData, TValue = TData[TKey]>({
  colDefProps: {
    Editor,
    editorProps,
    editable,
    clearable,
    cellRenderer,
    field,
    dataMapper,
    closeOnChange,
    headerName,
    editorFooter,
    dropdownPadding,
  },
  rendererParams,
}: {
  colDefProps: EditableColDef<TData, TKey, TValue>;
  rendererParams: ICellRendererParams<TData, TValue>;
}) => {
  const { updateRow, rowMutationRunning } = useAgGridEditable<TData>();

  return (
    <EditableCell
      Editor={Editor}
      editorProps={
        typeof editorProps === 'function'
          ? isNotNil(rendererParams.data)
            ? editorProps(rendererParams.data)
            : undefined
          : editorProps
      }
      onSave={async (value) => {
        const data = rendererParams.node.data;
        if (isNotNil(data)) {
          await updateRow(
            (await dataMapper?.(data, value)) ?? ({ [field as TKey]: value } as Partial<TData>),
            rendererParams,
          );
        }
      }}
      initialValue={rendererParams.value}
      renderValue={
        // Unfortunately, AG Grid's `cellRenderer` is typed as `any`
        // oxlint-disable-next-line typescript/no-unsafe-call
        cellRenderer?.(rendererParams) ??
        // fallback to the formatted value if the cellRenderer doesn't exist
        rendererParams.valueFormatted
      }
      editable={
        rowMutationRunning !== rendererParams.node.id &&
        (typeof editable === 'boolean'
          ? editable
          : isNotNil(rendererParams.data)
            ? editable(rendererParams.data)
            : false)
      }
      clearable={clearable}
      closeOnChange={closeOnChange}
      placeholder={headerName}
      dropdownPadding={dropdownPadding}
      withBorder={false}
      fullWidth
      footer={
        typeof editorFooter === 'function'
          ? isNotNil(rendererParams.data) && editorFooter(rendererParams.data)
          : editorFooter
      }
    />
  );
};

/**
 * Simple wrapper around `EditableCellPopover` to make it work as a `cellRenderer`.
 */
const EditableCellRenderer = <TData, TKey extends keyof TData, TValue>(props: EditableColDef<TData, TKey, TValue>) =>
  function (rendererParams: ICellRendererParams<TData, TValue>) {
    return <EditableCellPopover<TData, TKey, TValue> colDefProps={props} rendererParams={rendererParams} />;
  };

/**
 * Used to define an editable `ColDef`.
 *
 * Must be used inside an `AgGridEditable`.
 */
export const editableColDef = <
  TData,
  TKey extends keyof TData,
  TValue = TData[TKey],
  EComponent extends EditorComponent<TValue> = EditorComponent<TValue>,
>(
  props: EditableColDef<TData, TKey, TValue, EComponent>,
) => {
  const {
    field,
    Editor: _1,
    editable: _2,
    closeOnChange: _3,
    dataMapper: _4,
    editorProps: _5,
    editorFooter: _6,
    clearable: _7,
    dropdownPadding: _8,
    ...rest
  } = props;
  return {
    field,
    ...rest,
    cellRenderer: EditableCellRenderer(props),
  } as const satisfies ColDef<TData, TValue>;
};
